import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { PhoneMultiFactorGenerator, getAuth, RecaptchaVerifier, onAuthStateChanged, multiFactor, PhoneAuthProvider } from 'firebase/auth';
import { AppProvider } from './AppContext';
import { CacheProvider } from './CacheContext';
import LoginScreen from './LoginScreen';
import SignupScreen from './SignupScreen';
import AppWrapped from './AppWrapped';
import PrivacyPolicy from './PrivacyPolicy';
import MYDATprofileUser from './MYDATprofileUser';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { auth } from './firebase';
import './globalStyles.css';
import './index.css';
import LandingPage from './LandingPage';


const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState(null);

  const verifierRef = React.useRef(null);

  const [navigateRefresh, setNavigateRefresh] = useState(null);


  const navigate = useNavigate();
  const location = useLocation();


  
  const [isMFAEnabled, setIsMFAEnabled] = useState(false);
  const [showMFAPrompt, setShowMFAPrompt] = useState(false);
  const [showPhoneNumberInput, setShowPhoneNumberInput] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [verificationId, setVerificationId] = useState(null);

  useEffect(() => {
  // Open the native app from Instagram/Facebook in-app browsers for links like:
  // https://spndat.com/music/dats/bypassthemaster/False-God-Frequency?source=app
  // Falls back to store if the app isn't installed.

  const IOS_STORE_URL = 'https://apps.apple.com/us/app/spndat/id6503178936';
  const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.spndat.spndat';

  const isIGOrFBInApp = () => {
    const ua = navigator.userAgent || '';
    const ref = document.referrer || '';
    return /Instagram|FBAN|FBAV|FB_IAB/i.test(ua) || /instagram\.com|facebook\.com/i.test(ref);
  };

  try {
    const url = new URL(window.location.href);
    const pathname = url.pathname || '/';

    // Only auto-open when:
    // 1) inside IG/FB in-app browser,
    // 2) you explicitly opted in with ?source=app,
    // 3) on routes you want to deep link (music/* or userprofile/*).
    const optedIn = url.searchParams.get('source') === 'app';
    const onSupportedRoute =
      pathname.startsWith('/music/') || pathname.startsWith('/userprofile/');

    if (!isIGOrFBInApp() || !optedIn || !onSupportedRoute) return;

    // Build your custom-scheme deep link from the current URL:
    // e.g. spndat://music/dats/bypassthemaster/False-God-Frequency?source=app
    const schemeUrl = `spndat://${pathname.replace(/^\/+/, '')}${url.search}`;

    let didHide = false;
    const onVis = () => {
      if (document.hidden) didHide = true; // goes hidden if app successfully opened
    };
    document.addEventListener('visibilitychange', onVis);

    // Try to open the native app
    window.location.href = schemeUrl;

    // Fallback to the store (or your site) if app isn't installed
    const fallbackTimer = setTimeout(() => {
      if (!didHide) {
        const isiOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isiOS) {
          window.location.href = IOS_STORE_URL;
        } else if (/Android/i.test(navigator.userAgent)) {
          window.location.href = ANDROID_STORE_URL;
        } else {
          window.location.href = 'https://spndat.com';
        }
      }
    }, 1200);

    return () => {
      clearTimeout(fallbackTimer);
      document.removeEventListener('visibilitychange', onVis);
    };
  } catch {
    // ignore URL parse issues
  }
}, [location.pathname, location.search]);


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false); // Set loading to false once the auth state is determined
    });

    // Set a timeout to avoid long loading times
    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 10000); // Adjust the timeout duration as needed

    return () => {
      unsubscribe();
      clearTimeout(timeoutId); // Clear the timeout if the component unmounts
    };
  }, []);

  useEffect(() => {
    if (
      isLoggedIn &&
      !showMFAPrompt &&
      !showPhoneNumberInput &&
      !showCodeInput &&
      (location.pathname === '/login' || location.pathname === '/signup')
    ) {
      navigate('/');
    }
  }, [isLoggedIn, showMFAPrompt]);

  useEffect(() => {
    if (!window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth, // Place auth as the first parameter
          "recaptcha-container", // ID of the container element
          {
            size: "invisible", // Or "normal" for visible reCAPTCHA
            callback: (response) => {
              console.log("reCAPTCHA solved:", response);
            },
            "expired-callback": () => {
              console.warn("reCAPTCHA expired. Please try again.");
            },
          }
        );
  
        // Render the reCAPTCHA widget
        window.recaptchaVerifier.render().then((widgetId) => {

        });
      } catch (error) {

      }
    }
  }, [showMFAPrompt]);
  
  
  const validatePhoneNumber = (phoneNumber) => {
    const phoneRegex = /^[+]?[1-9]\d{1,14}$/; // E.164 format
    return phoneRegex.test(phoneNumber);
  };
  
  

  const sendVerificationCode = async () => {

    if (!validatePhoneNumber(phoneNumber)) {
      alert("Invalid phone number format. Please enter a valid phone number.");
      return;
    }

    try {
      console.log("Starting sendVerificationCode...");
  
      if (!window.recaptchaVerifier) {
        console.error("RecaptchaVerifier not initialized.");
        return;
      }
  
      // Ensure the user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {

        return;
      }
  
      console.log("Current User:", currentUser);
  
      // Get the Multi-Factor Session
      console.log("Getting Multi-Factor Session...");
      const multiFactorSession = await multiFactor(currentUser).getSession();
      console.log("Multi-Factor Session:", multiFactorSession);
  
      // Send the SMS verification code
      console.log("Sending verification code to:", phoneNumber);
      const phoneAuthProvider = new PhoneAuthProvider(auth);
  
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        {
          phoneNumber,
          session: multiFactorSession,
        },
        window.recaptchaVerifier // Use the initialized reCAPTCHA verifier
      );
  
      console.log("Verification ID received:", verificationId);
  
      // Save the verification ID for later use
      setVerificationId(verificationId);
  
      alert("Verification code sent to your phone.");
    } catch (error) {
      console.error("Error sending verification code:", error);
      alert(`Error: ${error.message}`);
    }
  };
  
  

  const activateMFA = async () => {
    try {
      if (!verificationId || !verificationCode) {
        alert('Please enter the verification code.');
        return;
      }
  
      // Create a PhoneAuthCredential
      const phoneAuthCredential = PhoneAuthProvider.credential(verificationId, verificationCode);
  
      // Convert to a MultiFactorAssertion
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
  
      // Enroll the second factor
      await multiFactor(auth.currentUser).enroll(multiFactorAssertion, 'My Phone Number');
  
      alert('MFA enabled successfully!');
      setShowCodeInput(false);
    } catch (error) { 
      console.error('Error enabling MFA:', error.message);
      alert('Failed to enable MFA. Please check the verification code and try again.');
    }
  }; 
  

    const handleButtonClick = () => {

    };


  const loadingStyles = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#0e061e',
    color: 'white',
    fontSize: '24px',
  };

  if (loading) {
    return (
      <div style={loadingStyles}>
        <div>Loading...</div>
      </div>
    );
  }



  return (
    <AppProvider>
      <CacheProvider>
        <div className="app-container">
          <div id="recaptcha-container"></div>
  
          {isLoggedIn && (
            <>
              <AppWrapped
                clientSecret={clientSecret}
                setClientSecret={setClientSecret}
                isMFAEnabled={isMFAEnabled}
                setIsMFAEnabled={setIsMFAEnabled}
                setShowMFAPrompt={setShowMFAPrompt}
                setNavigateRefreshHandler={setNavigateRefresh}
                user={user}
              />
              {/* MFA Prompts */}
              {showMFAPrompt && (
                <div style={styles.overlay}>
                  <div style={styles.mfaPrompt}>
                    <h2 style={styles.promptText}>
                      Enable Multi-Factor Authentication for Added Security
                    </h2>
                    <button
                      style={styles.button}
                      onClick={() => {
                        setShowMFAPrompt(false);
                        setShowPhoneNumberInput(true);
                      }}
                    >
                      Enable MFA 
                    </button>
                    <button
                      style={styles.secondaryButton}
                      onClick={() => {
                        setShowMFAPrompt(false);
                        handleButtonClick();
                        setIsLoggedIn(true);
                      }}
                    >
                      Skip for Now
                    </button>
                  </div>
                </div>
              )}
              {showPhoneNumberInput && (
                <div style={styles.overlay}>
                  <div style={styles.mfaPrompt}>
                    <h2 style={styles.promptText}>Enter Your Phone Number</h2>
                    <input
                      type="text"
                      placeholder="Phone Number"
                      style={styles.input}
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                    <button
                      style={styles.button}
                      onClick={() => {
                        setShowPhoneNumberInput(false);
                        setShowCodeInput(true);
                        sendVerificationCode();
                      }}
                    >
                      Next
                    </button>
                    <button
                      style={styles.secondaryButton}
                      onClick={() => setShowPhoneNumberInput(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {showCodeInput && (
                <div style={styles.overlay}>
                  <div style={styles.mfaPrompt}>
                    <h2 style={styles.promptText}>Enter Verification Code</h2>
                    <input
                      type="text"
                      placeholder="Verification Code"
                      style={styles.input}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                    />
                    <button style={styles.button} onClick={activateMFA}>
                      Activate MFA
                    </button>
                    <button
                      style={styles.secondaryButton}
                      onClick={() => {
                        setShowCodeInput(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </CacheProvider>
    </AppProvider>
  );
  
  
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  mfaPrompt: {
    backgroundColor: 'rgba(39, 18, 77, 0.9)',
    padding: '20px',
    borderRadius: '10px',
    textAlign: 'center',
    width: '80%',
    maxWidth: '400px',
  },
  promptText: {
    color: '#FFF',
    marginBottom: '16px',
    fontSize: '18px',
  },
  input: {
    width: '100%',
    padding: '10px',
    marginBottom: '16px',
    borderRadius: '5px',
    border: '1px solid #ddd',
  },
  button: {
    backgroundColor: 'transparent',
    color: '#62fbfa',
    border: '1px solid #62fbfa',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    margin: '5px',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    color: '#62fbfa',
    border: '1px solid #62fbfa',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    margin: '5px',
  },
};

export default App;
